import { useState, useEffect } from 'react';
import { api } from '../../../api/client';
import { ShoppingCart, Activity, ArrowDownUp } from 'lucide-react';
import { ConvertToInternalModal } from '../../../components/stock/ConvertToInternalModal';
import { useCan } from '../../../lib/rbac';

interface Purchase {
  id: number;
  ml_order_id: string;
  asin: string;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  amazon_status: string;
  stock_po_id: number | null;
  created_at: string;
  updated_at: string;
  buyer_nickname: string;
}

interface Movement {
  id: number;
  movement_type: string;
  quantity: number;
  balance_after: number;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
}

interface PurchasesTabProps {
  product: any;
  sellerId: number;
}

const AMAZON_STATUS_COLORS: Record<string, string> = {
  ordered: 'bg-blue-100 text-blue-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
};

const MOVEMENT_TYPE_CONFIG: Record<string, { label: string; color: string; sign: string }> = {
  purchase_received: { label: 'Compra recibida', color: 'bg-green-100 text-green-800', sign: '+' },
  sale_shipped: { label: 'Venta enviada', color: 'bg-red-100 text-red-800', sign: '-' },
  adjustment: { label: 'Ajuste', color: 'bg-yellow-100 text-yellow-800', sign: '' },
  manual_add: { label: 'Ingreso manual', color: 'bg-blue-100 text-blue-800', sign: '+' },
  manual_remove: { label: 'Retiro manual', color: 'bg-orange-100 text-orange-800', sign: '-' },
};

export default function PurchasesTab({ product, sellerId }: PurchasesTabProps) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [convertTarget, setConvertTarget] = useState<{
    stockProductId: number; productTitle: string; productAsin: string | null;
    qtyOnHand: number; unitCost: number; purchaseId: number;
  } | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loadingPurchases, setLoadingPurchases] = useState(true);
  const [loadingMovements, setLoadingMovements] = useState(true);

  useEffect(() => {
    const loadPurchases = async () => {
      setLoadingPurchases(true);
      try {
        const res = await api.get<Purchase[]>(`/catalog/products/${product.item_id}/purchases?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setPurchases(res.data);
        }
      } finally {
        setLoadingPurchases(false);
      }
    };

    const loadMovements = async () => {
      setLoadingMovements(true);
      try {
        const res = await api.get<Movement[]>(`/catalog/products/${product.item_id}/movements?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setMovements(res.data);
        }
      } finally {
        setLoadingMovements(false);
      }
    };

    loadPurchases();
    loadMovements();
  }, [product.item_id, sellerId]);

  return (
    <div className="space-y-6">
      {/* Purchases Section */}
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          <ShoppingCart size={16} />
          Compras Amazon
        </h3>

        {loadingPurchases ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-text-muted)] text-sm">
            No hay compras registradas para este producto
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-light)]">
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Fecha</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Orden ML</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">ASIN</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Qty</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Costo Unit</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Total</th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Status Amazon</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Comprador</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)]">
                    <td className="py-2 px-2 text-[var(--color-text)]">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-2 text-[var(--color-text)] font-mono text-xs">
                      {p.ml_order_id}
                    </td>
                    <td className="py-2 px-2">
                      <a
                        href={`https://www.amazon.com/dp/${p.asin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-primary)] hover:underline font-mono text-xs"
                      >
                        {p.asin}
                      </a>
                    </td>
                    <td className="py-2 px-2 text-right text-[var(--color-text)]">{p.quantity}</td>
                    <td className="py-2 px-2 text-right text-[var(--color-text)]">
                      {canMoney ? `$${Number(p.unit_cost).toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2 px-2 text-right font-medium text-[var(--color-text)]">
                      {canMoney ? `$${Number(p.total_cost).toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${AMAZON_STATUS_COLORS[p.amazon_status] || 'bg-gray-100 text-gray-800'}`}>
                        {p.amazon_status}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-[var(--color-text-muted)] text-xs">
                      {p.buyer_nickname || '—'}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {p.amazon_status === 'delivered' && p.asin && can('purchases.operate_internal_stock') && (
                        <button
                          onClick={async () => {
                            const lookup = await api.get<{ product_id: number; title: string; qty_on_hand: number }>(
                              `/internal-stock/product-by-asin?asin=${p.asin}&seller_id=${sellerId}`
                            );
                            if (!lookup.success || !lookup.data || lookup.data.qty_on_hand <= 0) {
                              alert('No hay stock on_hand para convertir');
                              return;
                            }
                            setConvertTarget({
                              stockProductId: lookup.data.product_id,
                              productTitle: lookup.data.title || p.asin,
                              productAsin: p.asin,
                              qtyOnHand: lookup.data.qty_on_hand,
                              unitCost: Number(p.unit_cost),
                              purchaseId: p.id,
                            });
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-700"
                          title="Convertir a Stock Interno"
                        >
                          <ArrowDownUp size={12} /> Convertir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Convert modal */}
      {convertTarget && (
        <ConvertToInternalModal
          isOpen={true}
          onClose={() => setConvertTarget(null)}
          onSuccess={() => setConvertTarget(null)}
          sellerId={sellerId}
          stockProductId={convertTarget.stockProductId}
          productTitle={convertTarget.productTitle}
          productAsin={convertTarget.productAsin}
          qtyOnHand={convertTarget.qtyOnHand}
          defaultQty={1}
          lockQty={true}
          defaultUnitCost={canMoney ? convertTarget.unitCost : 0}
          source={{ type: 'manual_purchase', purchase_id: convertTarget.purchaseId }}
        />
      )}

      {/* Movements Section */}
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          <Activity size={16} />
          Movimientos de Stock
        </h3>

        {loadingMovements ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : movements.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-text-muted)] text-sm">
            No hay movimientos registrados para este producto
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border-light)]">
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Fecha</th>
                  <th className="text-center py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Tipo</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Qty</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Balance</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Referencia</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[var(--color-text-muted)]">Notas</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => {
                  const config = MOVEMENT_TYPE_CONFIG[m.movement_type] || {
                    label: m.movement_type,
                    color: 'bg-gray-100 text-gray-800',
                    sign: '',
                  };
                  const isPositive = m.quantity > 0;

                  return (
                    <tr key={m.id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)]">
                      <td className="py-2 px-2 text-[var(--color-text)]">
                        {new Date(m.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                          {config.label}
                        </span>
                      </td>
                      <td className={`py-2 px-2 text-right font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                        {config.sign}{m.quantity}
                      </td>
                      <td className="py-2 px-2 text-right text-[var(--color-text)]">
                        {m.balance_after}
                      </td>
                      <td className="py-2 px-2 text-[var(--color-text-muted)] text-xs">
                        {m.reference_type && m.reference_id
                          ? `${m.reference_type}:${m.reference_id}`
                          : '—'}
                      </td>
                      <td className="py-2 px-2 text-[var(--color-text-muted)] text-xs">
                        {m.notes || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
